import { Content, Link, LinkVariant, Spacer } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo, useRef } from 'react'

import { useMediaQuery } from '../../hooks/useMediaQuery'
import { MEDIA_QUERIES } from '../../theme/media-queries'
import { Image } from '../Image/Image'
import { ComplexHeroProps } from './ComplexHero.type'

const solidClassByFacet: Record<string, string> = {
  [BackgroundColor.BERRY]: 'u-bgColorBerry',
  [BackgroundColor.PURPLE]: 'u-bgColorPurple',
  [BackgroundColor.MIDNIGHT_BLUE]: 'u-bgColorMidnightBlue',
  [BackgroundColor.LIGHT_BLUE]: 'u-bgColorAmpfBlue',
  [BackgroundColor.DARK_MINT]: 'u-bgColorDarkMint',
  [BackgroundColor.DARK_GREY]: 'u-bgColorDarkGrey',
  [BackgroundColor.NONE]: 'u-bgColorStone',
  [BackgroundColor.WHITE]: 'u-bgColorStone'
}

export const ComplexHeroWithCutout = (props: ComplexHeroProps) => {
  const {
    buttonTarget,
    buttonText,
    buttonUrl,
    content,
    featuredImage,
    featuredImageMobile,
    heading,
    backgroundFacet,
    componentSpacing,
    testId,
    analytics,
    psnAnalytics,
    abtestContainer
  } = props

  const headingRef = useRef<HTMLHeadingElement>(null)

  const isMobile = useMediaQuery(MEDIA_QUERIES.sm)
  const isTablet = useMediaQuery(MEDIA_QUERIES.md)
  const isDesktop = !isMobile && !isTablet

  const atb = useMemo(() => {
    return new AnalyticsTagBuilder()
      .setDefaultName('ComplexHero')
      .setAnalyticsName(analytics)
      .setPsnAnalytics(psnAnalytics)
      .setAbTestAnalytics(abtestContainer)
  }, [abtestContainer, analytics, psnAnalytics])

  const facetColor = backgroundFacet || BackgroundColor.MIDNIGHT_BLUE
  const facetDesktop = `${window.AMPCOMPATH}images/facets/heroes/complex-hero/desktop/${facetColor}.svg`
  const isContentReversed = facetColor !== BackgroundColor.WHITE

  const solidClass = solidClassByFacet[facetColor] || 'u-bgColorMidnightBlue'
  const contentClassName = ['ComplexHero-content', !isDesktop ? solidClass : '']
    .filter(Boolean)
    .join(' ')

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className={`ComplexHero ComplexHero--withCutout`}
        data-testid={testId ? `${testId}` : null}
        data-analytics-container="Hero"
      >
        <div className="u-flex u-sm-flexColReverse u-md-flexColReverse u-posRelative">
          <Image
            className="ComplexHero-background u-posAbsolute u-md-hidden"
            src={facetDesktop}
            sm={`${window.AMPCOMPATH}images/facets/heroes/complex-hero/mobile/${facetColor}.svg`}
            priority={true}
            testId={testId ? `${testId}-facet` : undefined}
          />
          <div
            className="ComplexHero-mobileOverlay u-lg-size6of12"
            style={{
              ...(isTablet && {
                backgroundImage: `url(${facetDesktop})`
              })
            }}
          >
            <Content
              reversed={isContentReversed}
              className={contentClassName}
              testId={testId ? `${testId}-content` : undefined}
            >
              <h1
                ref={headingRef}
                className="u-textTight"
                dangerouslySetInnerHTML={{ __html: heading || '' }}
                data-testid={testId ? `${testId}-heading` : null}
              ></h1>
              <div
                className="u-marginTop12 u-textDefault"
                dangerouslySetInnerHTML={{ __html: content || '' }}
                data-testid={testId ? `${testId}-body` : null}
              />
              {buttonText && buttonUrl && (
                <Link
                  className={
                    isContentReversed
                      ? 'ComplexHero-button Button--reversed u-marginTop32'
                      : 'ComplexHero-button u-marginTop32'
                  }
                  variant={LinkVariant.SECONDARY}
                  name={buttonText}
                  url={buttonUrl}
                  target={buttonTarget}
                  testId={testId ? `${testId}-link` : undefined}
                  {...atb.dataAnalyticAttribute(analytics)}
                />
              )}
            </Content>
          </div>
          <div className="ComplexHero-topWrapper u-flex u-flexJustifyCenter u-lg-size6of12 u-posRelative">
            <Image
              className="ComplexHero-topWrapperFacet u-posAbsolute u-sm-hidden u-lg-hidden"
              src={facetDesktop}
              priority={true}
              testId={testId ? `${testId}-tablet-facet` : undefined}
            />
            <Image
              className="ComplexHero-featuredImage u-posRelative"
              src={featuredImage || ''}
              sm={featuredImageMobile}
              priority={true}
              loading={'eager'}
              testId={testId ? `${testId}-featured-image` : undefined}
            />
          </div>
        </div>
      </section>
    </Spacer>
  )
}
